import React, {
  useState, useContext, useEffect, useRef, useMemo
} from 'react';
import { ExperienceContext } from '@thd-nucleus/experience-context';
import {
  useDataModel,
  extend
} from '@thd-nucleus/data-sources';
import PropTypes, { func } from 'prop-types';
import { Col, Row } from '@thd-olt-component-react/core-ui';
import { CompareSearchNavLoader } from '../../loaders/CompareSearchNavLoader';
import { CompareSpecifications } from '../compare-specifications/CompareSpecifications';
import { ComparePageHeader } from '../compare-page-header/ComparePageHeader';
import { ComparePods } from '../compare-pods/ComparePods';
// eslint-disable-next-line import/no-cycle
import { CompareCatalogLoader } from '../../loaders/CompareCatalogLoader';
import './full-compare.scss';

const getUrlSearchParams = (queryString) => {
  return {
    get: (param) => {
      const searchParam = new RegExp('[?&]' + param + '=([^&#]*)').exec(queryString);
      return searchParam && decodeURI(searchParam[1]);
    }
  };
};

const FullCompare = (props) => {
  const { location, navigate, predefinedItems } = props;
  const isBrowser = typeof window !== 'undefined';
  const ref = useRef();
  const {
    channel,
    store: { storeId },
    deliveryZip
  } = useContext(ExperienceContext);
  const [hideSimilar, setHideSimilar] = useState(false);
  const [setScrollPos] = useState({ xpos: 0, ypos: 0 });
  const { search: queryString } = location;

  const urlParams = useMemo(() => {
    if (predefinedItems.length > 0) {
      // Create a URLSearchParams object from the predefinedItems
      const parameters = new URLSearchParams();
      predefinedItems.forEach((item, idx) => {
        parameters.set(`item${idx + 1}`, item);
      });
      return parameters;
    }

    return isBrowser && window.URLSearchParams
      ? new window.URLSearchParams(queryString)
      : getUrlSearchParams(queryString);
  }, [queryString]);

  const items = useMemo(() => {
    return [
      urlParams.get('item1'),
      urlParams.get('item2'),
      urlParams.get('item3'),
      urlParams.get('item4')
    ].filter((itemId) => itemId);
  }, [urlParams]);

  const [itemIds, setItemIds] = useState(items);

  useMemo(() => {
    if (items.length) {
      setItemIds(items);
    }
  }, [items]);

  if (isBrowser) {
    window.sessionStorage.setItem('compare', JSON.stringify(itemIds));
  }

  const scrolled = () => {
    setScrollPos({
      xpos: ref.current.scrollLeft,
      ypos: ref.current.scrollTop
    });
  };

  const getInStoreFilter = () => {
    const isInStoreFilter = [urlParams.get('inStoreFilter')].filter(
      (inStoreFilter) => inStoreFilter
    );
    return isInStoreFilter[0] === 'true';
  };

  const updateQueryParams = (compareItems) => {
    let queryparams = compareItems.map((item, idx) => `item${idx + 1}=${item}`).join('&');

    queryparams += getInStoreFilter() ? '&inStoreFilter=true' : '';

    navigate(`/compare?${queryparams}`, { replace: true });
  };

  const onRemove = (id) => {
    const updatedItems = itemIds.filter((item) => item !== id);
    setItemIds(updatedItems);
    updateQueryParams(updatedItems);
  };

  const onBack = () => {
    navigate(-1);
  };

  const stringifyItemIds = (hookData) => {
    return hookData?.searchModel?.products?.map(({ itemId: sku }) => sku).join('-');
  };

  const { data, loading, error } = useDataModel('searchModel', {
    variables: {
      itemIds,
      storeId,
      additionalSearchParams: {
        deliveryZip: deliveryZip || '30339', // 30339 in order to match the mock on the tests
      }
    },
    ssr: false,
    skip: !itemIds.length
  });

  useEffect(() => {
    if (data && !loading) {
      LIFE_CYCLE_EVENT_BUS.lifeCycle.trigger('compare.new-products', data?.searchModel);
    }
  }, [stringifyItemIds(data)]);

  if (!data || loading || (error && !data)) {
    return null;
  }

  return (
    <Row className="isBound compare">
      <Col nopadding={channel !== 'desktop'} fallback={12}>
        <Row className="no-auto">
          <Col nopadding fallback={12}>
            <ComparePageHeader onBack={onBack} />
          </Col>
        </Row>
        <div className="compare__wrapper" ref={ref} onScroll={scrolled}>
          <Col
            nopadding
            className={`compare__content ${'compare__content--' + itemIds.length}`}
            fallback={12}
          >
            <CompareSearchNavLoader
              itemIds={itemIds}
              storeId={storeId}
              deliveryZip={deliveryZip || '30339'}
            >
              {() => {
                return (
                  <ComparePods
                    itemIds={itemIds}
                    onRemove={onRemove}
                    onHideSimilar={() => {
                      setHideSimilar(!hideSimilar);
                    }}
                    hasInStoreFilter={getInStoreFilter()}
                    hideSimilar={hideSimilar}
                  />
                );
              }}
            </CompareSearchNavLoader>
            <CompareCatalogLoader itemIds={itemIds} storeId={storeId}>
              {() => {
                return <CompareSpecifications itemIds={itemIds} hideSimilar={hideSimilar} />;
              }}
            </CompareCatalogLoader>
          </Col>
        </div>
      </Col>
    </Row>
  );
};

FullCompare.displayName = 'FullCompare';

FullCompare.propTypes = {
  location: PropTypes.shape({
    pathname: PropTypes.string.isRequired,
    search: PropTypes.string.isRequired
  }).isRequired,
  navigate: func.isRequired,
  predefinedItems: PropTypes.arrayOf(PropTypes.string)
};

FullCompare.defaultProps = {
  predefinedItems: []
};

FullCompare.dataModel = extend({}, CompareSearchNavLoader, CompareSpecifications);
export { FullCompare };
